import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp } from "lucide-react";
import { useSubscription } from "@/context/SubscriptionContext";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine, Tooltip, ResponsiveContainer } from "recharts";

const CHART_TYPES = ["xbar_r", "xbar_s", "imr", "p", "np", "c", "u"];

function SPCPlansTab() {
  const [plans, setPlans] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [chart, setChart] = useState(null);
  const empty = { item_id: "", characteristic: "", chart_type: "xbar_r", target: 10, lsl: 9.9, usl: 10.1, subgroup_size: 5, unit: "mm" };
  const [form, setForm] = useState(empty);

  const load = async () => {
    setPlans((await api.get("/quality/spc-plans")).data);
    setItems((await api.get("/items")).data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.item_id || !form.characteristic) return toast.error("Item and characteristic required");
    try { await api.post("/quality/spc-plans", form); toast.success("Saved"); setOpen(false); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { if (!window.confirm("Delete plan?")) return; await api.delete(`/quality/spc-plans/${id}`); load(); };
  const compute = async (p) => {
    try {
      const from_date = new Date(Date.now() - 30 * 86400e3).toISOString().slice(0, 10);
      const to_date = new Date().toISOString().slice(0, 10);
      const r = await api.get(`/quality/spc-plans/${p.id}/chart`, { params: { from_date, to_date } });
      setChart({ plan: p, ...r.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const chartData = chart?.chart?.x_bars ? chart.chart.x_bars.map((v, i) => ({ i: i + 1, xbar: v, r: chart.chart.ranges?.[i] })) : [];

  return (
    <div className="space-y-3" data-testid="spc-plans-tab">
      <div className="flex justify-end"><Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="new-spc-btn"><Plus className="w-3.5 h-3.5" /> SPC plan</Button></div>
      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Item</th><th>Characteristic</th><th>Chart</th><th className="text-right">Target</th><th className="text-right">LSL</th><th className="text-right">USL</th><th className="text-right">n</th><th></th></tr></thead>
          <tbody>
            {plans.length === 0 && <tr><td colSpan="8" className="text-center py-10 text-muted-foreground">No SPC plans yet.</td></tr>}
            {plans.map((p) => (
              <tr key={p.id} data-testid={`spc-plan-${p.characteristic}`}>
                <td>{items.find((i) => i.id === p.item_id)?.name || "—"}</td>
                <td className="font-medium">{p.characteristic}</td>
                <td><span className="text-[10px] font-mono uppercase text-accent">{p.chart_type}</span></td>
                <td className="text-right tabular-nums">{p.target}</td>
                <td className="text-right tabular-nums">{p.lsl}</td>
                <td className="text-right tabular-nums">{p.usl}</td>
                <td className="text-right tabular-nums">{p.subgroup_size}</td>
                <td className="text-right">
                  <Button size="sm" variant="ghost" className="h-7 text-xs gap-1" onClick={() => compute(p)} data-testid={`spc-compute-${p.characteristic}`}><TrendingUp className="w-3 h-3" /> Chart</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-red-500" onClick={() => del(p.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create plan */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader><SheetTitle className="font-heading">New SPC Plan</SheetTitle></SheetHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Item</Label>
              <Select value={form.item_id} onValueChange={(v) => setForm({ ...form, item_id: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="spc-item"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">{items.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</div></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Characteristic (e.g. dia_mm)</Label><Input value={form.characteristic} onChange={(e) => setForm({ ...form, characteristic: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="spc-char" /></div>
            <div><Label>Chart type</Label>
              <Select value={form.chart_type} onValueChange={(v) => setForm({ ...form, chart_type: v })}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{CHART_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Subgroup size</Label><Input type="number" value={form.subgroup_size} onChange={(e) => setForm({ ...form, subgroup_size: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Target</Label><Input type="number" step="0.001" value={form.target} onChange={(e) => setForm({ ...form, target: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>Unit</Label><Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>LSL</Label><Input type="number" step="0.001" value={form.lsl} onChange={(e) => setForm({ ...form, lsl: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
            <div><Label>USL</Label><Input type="number" step="0.001" value={form.usl} onChange={(e) => setForm({ ...form, usl: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums text-right" /></div>
          </div>
          <Button onClick={save} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="spc-save">Save</Button>
        </SheetContent>
      </Sheet>

      {/* Chart viewer */}
      <Sheet open={!!chart} onOpenChange={(o) => !o && setChart(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none space-y-3 overflow-y-auto">
          {chart && (
            <>
              <SheetHeader><SheetTitle className="font-heading">Chart · {chart.plan?.characteristic}</SheetTitle></SheetHeader>
              {chart.chart?.error && <div className="text-red-500 text-sm">{chart.chart.error}</div>}
              {chartData.length > 0 && (
                <>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">X̄ chart</div>
                  <div className="h-64">
                    <ResponsiveContainer><LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="i" /><YAxis />
                      <Tooltip />
                      <Line type="monotone" dataKey="xbar" stroke="#f97316" strokeWidth={2} dot />
                      <ReferenceLine y={chart.chart.x_bar_bar} stroke="#666" strokeDasharray="4 4" label="X̄̄" />
                      <ReferenceLine y={chart.chart.ucl_x} stroke="#ef4444" label="UCL" />
                      <ReferenceLine y={chart.chart.lcl_x} stroke="#ef4444" label="LCL" />
                    </LineChart></ResponsiveContainer>
                  </div>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground mt-4">R chart</div>
                  <div className="h-48">
                    <ResponsiveContainer><LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" /><XAxis dataKey="i" /><YAxis /><Tooltip />
                      <Line type="monotone" dataKey="r" stroke="#3b82f6" strokeWidth={2} dot />
                      <ReferenceLine y={chart.chart.r_bar} stroke="#666" strokeDasharray="4 4" label="R̄" />
                      <ReferenceLine y={chart.chart.ucl_r} stroke="#ef4444" label="UCL" />
                    </LineChart></ResponsiveContainer>
                  </div>
                </>
              )}
              {chart.capability && !chart.capability.error && (
                <div className="grid grid-cols-4 gap-3 mt-4">
                  <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Cp</div><div className="text-xl tabular-nums">{chart.capability.cp}</div></div>
                  <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-accent">Cpk</div><div className={`text-2xl tabular-nums font-medium ${chart.capability.cpk >= 1.33 ? "text-emerald-600" : chart.capability.cpk >= 1 ? "text-yellow-500" : "text-red-500"}`}>{chart.capability.cpk}</div></div>
                  <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Cpm</div><div className="text-xl tabular-nums">{chart.capability.cpm ?? "—"}</div></div>
                  <div className="card-sharp p-3"><div className="text-[10px] font-mono uppercase text-muted-foreground">Within spec</div><div className="text-xl tabular-nums">{chart.capability.within_spec_pct}%</div></div>
                </div>
              )}
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function InspectionsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/quality/inspections").then((r) => setRows(r.data)); }, []);
  return (
    <div className="card-sharp overflow-hidden">
      <table className="w-full dense">
        <thead><tr><th>Number</th><th>Date</th><th>Ref</th><th className="text-right">Insp</th><th className="text-right">Pass</th><th className="text-right">Rej</th><th>Status</th></tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No inspections yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="font-mono text-xs">{r.inspection_number}</td>
              <td className="text-xs">{r.date}</td>
              <td className="text-xs font-mono text-muted-foreground">{r.reference_type}</td>
              <td className="text-right tabular-nums text-xs">{r.qty_inspected}</td>
              <td className="text-right tabular-nums text-xs text-emerald-600">{r.qty_passed}</td>
              <td className="text-right tabular-nums text-xs text-red-500">{r.qty_rejected}</td>
              <td><span className="text-[10px] font-mono uppercase text-accent">{r.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Quality() {
  const { hasModule } = useSubscription();
  if (!hasModule("quality")) return <div className="p-8"><div className="card-sharp p-10 text-center"><div className="font-heading text-2xl">Quality & SPC require Enterprise Manufacturing</div><a href="/app/subscription" className="inline-block mt-4 px-5 py-2 bg-accent text-white rounded-sm text-sm">Upgrade →</a></div></div>;
  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="quality-page">
      <PageHeader eyebrow="Manufacturing" title="Quality · SPC"
        description="Statistical Process Control — X̄-R, X̄-s, I-MR, p/np/c/u charts with Cp/Cpk capability analysis. Native Python engine, no numpy required." />
      <Tabs defaultValue="plans">
        <TabsList className="rounded-sm">
          <TabsTrigger value="plans" className="rounded-sm" data-testid="tab-spc-plans">SPC Plans</TabsTrigger>
          <TabsTrigger value="insp" className="rounded-sm" data-testid="tab-inspections">Inspections</TabsTrigger>
        </TabsList>
        <TabsContent value="plans" className="mt-4"><SPCPlansTab /></TabsContent>
        <TabsContent value="insp" className="mt-4"><InspectionsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
